import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import UrgencyBanner from "../components/UrgencyBanner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

export default function Profile() {
  const { user, loading, fetchUser, logout } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const isCompleting = params.get("complete") === "1";
  const nextPath = params.get("next") || "";

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [loading, user, nav]);

  if (!user) return null;

  const dismissBanner = () => {
    const p = new URLSearchParams(params);
    p.delete("complete");
    p.delete("next");
    setParams(p, { replace: true });
  };

  return (
    <div className="min-h-screen">
      <UrgencyBanner text="MY PROFILE // DROP SHOP LIVE // " />

      {/* Complete-profile banner (shown once after first-time login to /live/) */}
      {isCompleting && (
        <div className="bg-[#FF6A00] text-[#161616]" data-testid="profile-complete-banner">
          <div className="px-4 md:px-10 py-4 flex flex-col md:flex-row md:items-center gap-3 justify-between">
            <div className="flex items-start gap-3 min-w-0">
              <span className="font-display text-2xl leading-none">◆</span>
              <div className="min-w-0">
                <div className="font-display text-lg md:text-xl uppercase tracking-tight leading-tight">
                  Welcome to Drop Shop Live — one last step
                </div>
                <div className="font-mono-ds text-[11px] uppercase tracking-widest opacity-90 mt-1 break-words">
                  Add a shipping address so you can buy the moment the price hits its floor. Under 30 seconds.
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={dismissBanner}
              className="font-mono-ds text-[11px] uppercase tracking-widest border border-white/50 hover:border-white text-[#161616] px-3 py-1.5 whitespace-nowrap self-start md:self-auto"
              data-testid="profile-complete-skip"
            >
              Skip for now
            </button>
          </div>
        </div>
      )}

      <section className="px-4 md:px-10 py-10 grid-bg border-b border-[#E5E1D8]">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 border border-[#E5E1D8] bg-[#FFFFFF] overflow-hidden flex items-center justify-center">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <span className="font-display text-3xl text-[#FF6A00]">
                  {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                </span>
              )}
            </div>
            <div>
              <div className="brand-tag">// MEMBER</div>
              <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tighter mt-1 leading-[0.9]" data-testid="profile-name">
                {user.name || "Anonymous"}
              </h1>
              <div className="mt-1 font-mono-ds text-xs text-[#6E6E6E]" data-testid="profile-email">{user.email}</div>
            </div>
          </div>
          <Button
            onClick={async () => { await logout(); nav("/"); }}
            variant="outline"
            className="rounded-2xl border-[#E5E1D8] hover:border-[#FF6A00] hover:text-[#FF6A00] bg-transparent text-[#161616] font-mono-ds uppercase text-xs tracking-widest"
            data-testid="profile-logout-btn"
          >
            Sign Out →
          </Button>
        </div>
      </section>

      <section className="px-4 md:px-10 py-8">
        <Tabs defaultValue={isCompleting ? "addresses" : "details"} className="w-full">
          <TabsList className="bg-transparent border border-[#E5E1D8] rounded-2xl p-1 h-auto inline-flex" data-testid="profile-tabs">
            <TabTrig value="details" label="Details" testId="tab-details" />
            <TabTrig value="addresses" label={isCompleting ? "Addresses ◆" : "Addresses"} testId="tab-addresses" />
            <TabTrig value="orders" label="Orders" testId="tab-orders" />
          </TabsList>

          <TabsContent value="details" className="mt-6">
            <DetailsPanel user={user} onUpdated={fetchUser} />
          </TabsContent>
          <TabsContent value="addresses" className="mt-6">
            <AddressesPanel completeNext={isCompleting ? nextPath : ""} />
          </TabsContent>
          <TabsContent value="orders" className="mt-6">
            <OrdersPanel />
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}

function TabTrig({ value, label, testId }) {
  return (
    <TabsTrigger
      value={value}
      data-testid={testId}
      className="rounded-2xl px-5 h-10 font-mono-ds text-[11px] uppercase tracking-[0.2em] text-[#6E6E6E] data-[state=active]:bg-[#FF6A00] data-[state=active]:text-[#161616] data-[state=active]:font-bold"
    >
      {label}
    </TabsTrigger>
  );
}

/* -------------------- Details -------------------- */
function DetailsPanel({ user, onUpdated }) {
  const [name, setName] = useState(user.name || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/auth/me", { name, phone });
      await onUpdated();
      toast.success("Profile updated");
    } catch (err) {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="border border-[#E5E1D8] bg-[#FFFFFF] p-6 md:p-8 max-w-2xl" data-testid="details-form">
      <div className="brand-tag">// IDENTITY</div>
      <h2 className="font-display text-3xl uppercase tracking-tight mt-1">Personal Details</h2>
      <p className="mt-1 font-mono-ds text-xs text-[#6E6E6E]">Email is locked to your Google account.</p>

      <div className="mt-8 grid sm:grid-cols-2 gap-5">
        <Field label="Name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2.5 font-mono-ds text-sm focus:border-[#FF6A00] outline-none"
            data-testid="details-name-input"
          />
        </Field>
        <Field label="Email (locked)">
          <input
            value={user.email}
            disabled
            className="w-full bg-[#FFFFFF] border border-[#E5E1D8] px-3 py-2.5 font-mono-ds text-sm text-[#6E6E6E] cursor-not-allowed"
          />
        </Field>
        <Field label="Phone">
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+91 ..."
            className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2.5 font-mono-ds text-sm focus:border-[#FF6A00] outline-none"
            data-testid="details-phone-input"
          />
        </Field>
        <Field label="Member since">
          <input
            value={new Date(user.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
            disabled
            className="w-full bg-[#FFFFFF] border border-[#E5E1D8] px-3 py-2.5 font-mono-ds text-sm text-[#6E6E6E] cursor-not-allowed"
          />
        </Field>
      </div>

      <Button
        type="submit"
        disabled={saving}
        className="mt-8 rounded-2xl bg-[#FF6A00] hover:bg-[#FFFFFF] hover:text-[#161616] text-[#161616] font-mono-ds uppercase tracking-widest h-11 px-8"
        data-testid="details-save-btn"
      >
        {saving ? "Saving..." : "Save Changes"}
      </Button>
    </form>
  );
}

/* -------------------- Addresses -------------------- */
function AddressesPanel({ completeNext = "" }) {
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    const r = await api.get("/addresses");
    setList(r.data);
    setLoaded(true);
  };
  useEffect(() => { load(); }, []);

  // Auto-open the "New Address" dialog only after the first fetch confirms
  // the user has no addresses yet AND they arrived via the profile-completion gate.
  useEffect(() => {
    if (loaded && completeNext && list.length === 0 && !open) {
      setEdit(null);
      setOpen(true);
    }
  }, [loaded, completeNext, list.length, open]);

  const remove = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    await api.delete(`/addresses/${id}`);
    toast.success("Deleted");
    load();
  };

  const canContinueToLive = completeNext && list.length > 0;

  return (
    <div>
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="brand-tag">// SHIPPING</div>
          <h2 className="font-display text-3xl uppercase tracking-tight mt-1">Saved Addresses</h2>
          {completeNext && list.length === 0 && (
            <p className="mt-2 text-[#6E6E6E] text-sm max-w-lg">
              Add your first address to unlock instant checkout during the live drop.
            </p>
          )}
        </div>
        <Button
          onClick={() => { setEdit(null); setOpen(true); }}
          className="rounded-2xl bg-[#FF6A00] hover:bg-[#FFFFFF] hover:text-[#161616] text-[#161616] font-mono-ds uppercase tracking-widest h-10 px-5"
          data-testid="new-address-btn"
        >
          + New Address
        </Button>
      </div>

      {list.length === 0 ? (
        <div className="border border-[#E5E1D8] bg-[#FFFFFF] p-10 text-center font-mono-ds text-[#6E6E6E]" data-testid="addresses-empty">
          // No addresses saved. Add one so we can ship your drops.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {list.map((a) => (
            <AddressCard
              key={a.address_id}
              addr={a}
              onEdit={() => { setEdit(a); setOpen(true); }}
              onDelete={() => remove(a.address_id)}
            />
          ))}
        </div>
      )}

      {/* Continue-to-live CTA — only shown when the user came from a login->live gate */}
      {canContinueToLive && (
        <div className="mt-6 border border-[#FF6A00] bg-[#FFFFFF] p-4 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid="continue-to-live-strip">
          <div className="min-w-0">
            <div className="brand-tag">// READY</div>
            <div className="font-display text-xl uppercase tracking-tight mt-1">
              You&apos;re set — jump into the drop
            </div>
            <div className="text-[#6E6E6E] text-sm mt-1">
              We&apos;ll ship to {list.find((a) => a.is_default)?.city || list[0]?.city || "your address"}. You can add more anytime.
            </div>
          </div>
          <Button
            onClick={() => nav(completeNext)}
            className="rounded-2xl bg-[#FF6A00] hover:bg-[#FF7B1A] text-[#161616] font-mono-ds uppercase tracking-widest h-12 px-6 font-bold whitespace-nowrap"
            data-testid="continue-to-live-btn"
          >
            Enter Live Room →
          </Button>
        </div>
      )}

      <AddressDialog open={open} setOpen={setOpen} edit={edit} onSaved={load} />
    </div>
  );
}

function AddressCard({ addr, onEdit, onDelete }) {
  return (
    <div
      className={`border ${addr.is_default ? "border-[#FF6A00]" : "border-[#E5E1D8]"} bg-[#FFFFFF] p-5 flex flex-col`}
      data-testid={`address-card-${addr.address_id}`}
    >
      <div className="flex items-center justify-between">
        <div className="font-mono-ds text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">{addr.label}</div>
        {addr.is_default && (
          <span className="brand-pill">
            Default
          </span>
        )}
      </div>
      <div className="mt-3 font-mono-ds text-sm leading-relaxed text-[#161616]">
        {addr.line1}
        {addr.line2 ? <><br />{addr.line2}</> : null}
        <br />{addr.city}, {addr.state} {addr.postal_code}
        <br />{addr.country}
      </div>
      {addr.phone && <div className="mt-2 font-mono-ds text-xs text-[#6E6E6E]">📞 {addr.phone}</div>}
      <div className="mt-auto pt-4 flex gap-3">
        <button onClick={onEdit} className="font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E] hover:text-[#FF6A00]" data-testid={`edit-address-${addr.address_id}`}>
          Edit
        </button>
        <button onClick={onDelete} className="font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E] hover:text-[#FF6A00]" data-testid={`delete-address-${addr.address_id}`}>
          Delete
        </button>
      </div>
    </div>
  );
}

function AddressDialog({ open, setOpen, edit, onSaved }) {
  const [form, setForm] = useState(emptyAddress());

  useEffect(() => {
    if (edit) setForm({ ...emptyAddress(), ...edit });
    else setForm(emptyAddress());
  }, [edit, open]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = {
        label: form.label, line1: form.line1, line2: form.line2 || "",
        city: form.city, state: form.state, postal_code: form.postal_code,
        country: form.country || "India", phone: form.phone || "",
        is_default: !!form.is_default,
      };
      if (edit) await api.put(`/addresses/${edit.address_id}`, body);
      else await api.post("/addresses", body);
      toast.success(edit ? "Address updated" : "Address added");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#FFFFFF] border border-[#E5E1D8] rounded-2xl max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight">
            {edit ? "Edit Address" : "New Address"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4 font-mono-ds text-sm" data-testid="address-form">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Label">
              <select
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                className="w-full bg-[#FFFFFF] border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none"
                data-testid="addr-label-input"
              >
                <option>Home</option>
                <option>Office</option>
                <option>Other</option>
              </select>
            </Field>
            <Field label="Phone">
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none"
                data-testid="addr-phone-input" />
            </Field>
          </div>
          <Field label="Address line 1">
            <input required value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })}
              className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none"
              data-testid="addr-line1-input" />
          </Field>
          <Field label="Address line 2">
            <input value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })}
              className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="City">
              <input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none"
                data-testid="addr-city-input" />
            </Field>
            <Field label="State">
              <input required value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
                className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none"
                data-testid="addr-state-input" />
            </Field>
            <Field label="Postal code">
              <input required value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none"
                data-testid="addr-postal-input" />
            </Field>
            <Field label="Country">
              <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none" />
            </Field>
          </div>
          <label className="flex items-center gap-2 cursor-pointer font-mono-ds text-xs uppercase tracking-widest text-[#6E6E6E]">
            <input
              type="checkbox"
              checked={!!form.is_default}
              onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
              className="accent-[#FF6A00]"
              data-testid="addr-default-checkbox"
            />
            Set as default address
          </label>
          <Button type="submit" className="w-full rounded-2xl bg-[#FF6A00] hover:bg-[#FFFFFF] hover:text-[#161616] text-[#161616] font-mono-ds uppercase tracking-widest h-11" data-testid="save-address-btn">
            Save Address
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function emptyAddress() {
  return { label: "Home", line1: "", line2: "", city: "", state: "", postal_code: "", country: "India", phone: "", is_default: false };
}

/* -------------------- Orders -------------------- */
function OrdersPanel() {
  const [orders, setOrders] = useState([]);

  const load = () => api.get("/bookings/me").then((r) => setOrders(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  // If any order is still pending_payment (common with UPI where the JS handler
  // may not fire after the app roundtrip), poll every 4 s until the webhook
  // confirms the booking. Poll stops as soon as no pending rows remain.
  useEffect(() => {
    const hasPending = orders.some((o) => o.status === "pending_payment");
    if (!hasPending) return;
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [orders]);

  return (
    <div>
      <div className="brand-tag">// ORDERS</div>
      <h2 className="font-display text-3xl uppercase tracking-tight mt-1 mb-6">My Orders</h2>
      {orders.some((o) => o.status === "pending_payment") && (
        <div className="mb-3 border border-[#FF6A00] bg-[#FFFFFF] px-3 py-2 flex items-center gap-2" data-testid="orders-pending-hint">
          <span className="w-2 h-2 bg-[#FF6A00] pulse-dot rounded-full" />
          <span className="font-mono-ds text-[11px] uppercase tracking-widest text-[#6E6E6E]">
            Confirming a payment · this page refreshes automatically
          </span>
        </div>
      )}
      <div className="border border-[#E5E1D8] bg-[#FFFFFF] overflow-x-auto">
        <table className="w-full text-sm font-mono-ds min-w-[640px]">
          <thead className="border-b border-[#E5E1D8] text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">
            <tr>
              <th className="text-left p-4">Booking</th>
              <th className="text-left p-4">Live</th>
              <th className="text-left p-4">Qty</th>
              <th className="text-left p-4">Total</th>
              <th className="text-left p-4">Ships to</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">When</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-[#6E6E6E]">// No orders yet. <Link to="/" className="text-[#FF6A00] hover:text-[#161616]">Browse drops →</Link></td></tr>
            )}
            {orders.map((b) => (
              <tr key={b.booking_id} className="border-b border-[#FFFFFF]" data-testid={`order-row-${b.booking_id}`}>
                <td className="p-4 text-[#161616]">{b.booking_id}</td>
                <td className="p-4 text-[#6E6E6E]"><Link className="hover:text-[#FF6A00]" to={`/live/${b.live_id}`}>{b.live_id}</Link></td>
                <td className="p-4 text-[#161616]">{b.quantity}</td>
                <td className="p-4 text-[#FF6A00] font-bold">₹{b.total}</td>
                <td className="p-4 text-[#6E6E6E]">
                  {b.shipping_address ? (
                    <div className="leading-tight">
                      <div className="text-[#161616] truncate max-w-[180px]">{b.shipping_address.line1}</div>
                      <div className="text-[10px] text-[#6E6E6E]">{b.shipping_address.city}, {b.shipping_address.postal_code}</div>
                    </div>
                  ) : (
                    <span className="text-[#6E6E6E]">—</span>
                  )}
                </td>
                <td className="p-4 brand-tag">{b.status}</td>
                <td className="p-4 text-[#6E6E6E] text-[10px]">{new Date(b.created_at).toLocaleString("en-IN")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-mono-ds text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
